import { LazyImage } from '@frontend/design_system'
import filledHeartIcon from 'assets/icons/blue700/blue700_heartFilled_16x16.webp'
import styles from './styles.module.scss'
import heartIcon from 'assets/icons/blue700/blue700_heart_16x16.webp'
import redHeartIcon from 'assets/icons/red600/red600_heart_20x20.webp'
import { useApiRequest } from 'hooks/useApiRequest'
import { profileApi } from 'api'
import { useDispatch } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { useMemo, useState } from 'react'
import { joinClasses } from 'utils/joinClasses'
import { Confirmation } from '../Confirmation'
import {
  COMPANY_WAS_ADDED_TO_FAVORITES,
  COMPANY_WAS_REMOVED_FROM_FAVORITES,
  getCompanyRemoveModalMessage,
  getCompanyRemoveModalTitle,
} from 'texts/favorites'

export const FavoriteButton = ({
  isFavorite,
  companyId,
  companyName,
  onFavoriteChange,
  variant = 'primary',
}: {
  isFavorite: boolean
  companyId: string
  companyName?: string
  onFavoriteChange: () => void
  variant?: 'primary' | 'secondary'
}) => {
  const dispatch = useDispatch()
  const [isAnimating, setIsAnimating] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const addToFavoritesRequest = useApiRequest((companyId) =>
    profileApi.addToFavorites(companyId)
  )

  const removeFromFavoritesRequest = useApiRequest((companyId) =>
    profileApi.removeFromFavorites(companyId)
  )

  const handleModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  const handleRemoveFromFavorites = async () => {
    const response = await removeFromFavoritesRequest.apiRequest(companyId)
    if (response) {
      dispatch(
        setNotificationMessage({
          notificationMessage: `${companyName} ${COMPANY_WAS_REMOVED_FROM_FAVORITES}`,
        })
      )
      onFavoriteChange()
    }
    handleModalClose()
  }

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const notificationMessage = `${companyName || 'This company'} ${
      isFavorite
        ? COMPANY_WAS_REMOVED_FROM_FAVORITES
        : COMPANY_WAS_ADDED_TO_FAVORITES
    }`
    let response
    if (isFavorite) {
      if (variant === 'secondary') {
        setIsConfirmationModalOpen(true)
        return
      }
      response = await removeFromFavoritesRequest.apiRequest(companyId)
    } else {
      response = await addToFavoritesRequest.apiRequest(companyId)
    }
    if (response) {
      dispatch(
        setNotificationMessage({
          notificationMessage,
        })
      )
      const animationDuration = 300
      if (!isAnimating && variant === 'primary' && !isFavorite) {
        setIsAnimating(true)
        setTimeout(() => {
          setIsAnimating(false)
        }, animationDuration)
      }
      onFavoriteChange()
    }
  }

  const getIconSrc = useMemo(() => {
    if (isFavorite) {
      return variant === 'primary' ? redHeartIcon : filledHeartIcon
    }
    return heartIcon
  }, [variant, isFavorite])

  return (
    <>
      {isConfirmationModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: getCompanyRemoveModalMessage(companyName),
            onConfirm: handleRemoveFromFavorites,
            onClose: handleModalClose,
            title: getCompanyRemoveModalTitle(companyName),
          }}
        />
      )}
      <div
        className={joinClasses(styles['button-container'], [
          styles.animating,
          isAnimating,
        ])}
      >
        <LazyImage
          className={styles.icon}
          width={16}
          height={16}
          src={getIconSrc}
          onClick={handleClick}
        />
      </div>
    </>
  )
}
